import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, renderList as _renderList } from "vue"

const _hoisted_1 = { class: "text-sm transition-colors hover:bg-gray-50" }
const _hoisted_2 = { class: "flex items-center gap-2" }
const _hoisted_3 = ["title", "aria-expanded", "aria-label"]
const _hoisted_4 = ["title"]
const _hoisted_5 = ["title"]
const _hoisted_6 = { class: "py-1 pr-2 tabular-nums" }
const _hoisted_7 = { class: "flex items-center gap-2" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = {
  key: 2,
  class: "text-[12.5px] font-medium text-gray-500"
}
const _hoisted_10 = { class: "py-1 pr-4 tabular-nums" }
const _hoisted_11 = { class: "py-1 pr-2 tabular-nums" }
const _hoisted_12 = ["title"]
const _hoisted_13 = { class: "py-1 pr-1 text-right tabular-nums" }
const _hoisted_14 = { class: "text-[11.5px] font-medium text-gray-400" }
const _hoisted_15 = { key: 0 }
const _hoisted_16 = {
  colspan: "5",
  class: "py-2 pl-6 text-xs italic text-gray-400"
}

import { computed } from 'vue';
import { ChevronRight, TrendingDown, TrendingUp } from 'lucide-vue-next';
import type { BarnObjectiveRow, TrendDirection } from './objectiveRow';
import { objectiveValueColourClass, pigsToTargetColourClass, pigsToTargetTooltipKey, formatPigsToTarget, trendDirectionColourClass } from './objectiveDisplay';
import { countDeviatingSections, isInDeviation, isWithinTarget } from './deviation';
import { isBarOverflow } from './objectiveBar';
import SectionRow from './SectionRow.vue';
import ObjectiveBar from './ObjectiveBar.vue';

const placeholder = '—';


export default /*@__PURE__*/_defineComponent({
  __name: 'BarnRow',
  props: {
    barn: {},
    expanded: { type: Boolean },
    ceiling: {},
    farmLocationId: {},
    trendDirection: {}
  },
  emits: ["toggle", "setTarget"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const valueColourClass = computed(() => objectiveValueColourClass(props.barn));
const deviatingSectionCount = computed(() => countDeviatingSections(props.barn.sections));

// No measured value at all (Empty/InsufficientPigsInWindow/DaysInPiggeryUnavailable/NoData) - greys
// the name out instead of the row carrying its own separate "No pigs in this window"-style note,
// since that note would read as an alarm rather than a data-quality caveat. The reason itself only
// ever surfaces on hover, folded into the same tooltip the name already had for truncation.
const hasNoValue = computed(() => props.barn.currentValue == null);
const nameTooltip = computed(() => (hasNoValue.value && props.barn.status.message ? `${props.barn.name} — ${props.barn.status.message}` : props.barn.name));

// Just the left-accent colour (see the template's own note on the grouped table) - the
// neutral case needs no override, the Name cell's own border-l-2 already defaults to a light neutral
// via Tailwind's default border colour.
const rowClasses = computed(() => {
    if (isInDeviation(props.barn)) return 'border-l-red-400';
    if (isWithinTarget(props.barn)) return 'border-l-green-400';
    return 'border-l-gray-100';
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("tr", _hoisted_1, [
      _createElementVNode("td", {
        class: _normalizeClass(["border-l-2 py-1 pl-6 pr-2", rowClasses.value])
      }, [
        _createElementVNode("div", _hoisted_2, [
          (_ctx.barn.isExpandable)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                class: "grid h-5 w-5 shrink-0 place-content-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600",
                title: _ctx.expanded ? _ctx.$t('views.objectives.texts.collapse') : _ctx.$t('views.objectives.texts.expand'),
                "aria-expanded": _ctx.expanded,
                "aria-label": `${_ctx.expanded ? _ctx.$t('views.objectives.texts.collapse') : _ctx.$t('views.objectives.texts.expand')}: ${_ctx.barn.name}`,
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('toggle')))
              }, [
                _createVNode(_unref(ChevronRight), {
                  class: _normalizeClass(["h-4 w-4 transition-transform", { 'rotate-90': _ctx.expanded }]),
                  "aria-hidden": "true"
                }, null, 8, ["class"])
              ], 8, _hoisted_3))
            : _createCommentVNode("", true),
          _createElementVNode("span", {
            class: _normalizeClass(["min-w-0 truncate text-[13.5px] font-semibold", hasNoValue.value ? 'text-gray-400' : 'text-gray-900']),
            title: nameTooltip.value
          }, _toDisplayString(_ctx.barn.name), 11, _hoisted_4),
          (deviatingSectionCount.value > 0)
            ? (_openBlock(), _createElementBlock("span", {
                key: 1,
                class: "grid h-5 min-w-[20px] shrink-0 place-content-center rounded-full bg-red-600 px-1 text-[11px] font-bold text-white",
                title: _ctx.$tc('views.objectives.texts.deviationBadgeTitle', deviatingSectionCount.value)
              }, _toDisplayString(deviatingSectionCount.value), 9, _hoisted_5))
            : _createCommentVNode("", true)
        ])
      ], 2),
      _createElementVNode("td", _hoisted_6, [
        _createElementVNode("div", _hoisted_7, [
          (_ctx.barn.currentValue != null && _ctx.barn.resolvedTarget != null)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(ObjectiveBar, {
                  "current-value": _ctx.barn.currentValue,
                  "resolved-target": _ctx.barn.resolvedTarget,
                  "target-source": _ctx.barn.status.target_source,
                  ceiling: _ctx.ceiling,
                  class: "w-20"
                }, null, 8, ["current-value", "resolved-target", "target-source", "ceiling"]),
                _createElementVNode("span", {
                  class: _normalizeClass(["text-[13.5px] font-bold leading-none", valueColourClass.value])
                }, [
                  _createTextVNode(_toDisplayString(_ctx.$n(_ctx.barn.currentValue, 'decimal')) + "%", 1),
                  (_unref(isBarOverflow)(_ctx.barn.currentValue, _ctx.ceiling))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, "»"))
                    : _createCommentVNode("", true)
                ], 2)
              ], 64))
            : (_ctx.barn.resolvedTarget == null)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  type: "button",
                  class: "shrink-0 rounded-md border border-primary-600 px-2 py-1 text-[12px] font-medium text-primary-700 transition-colors hover:bg-primary-50",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('setTarget', _ctx.farmLocationId)))
                }, _toDisplayString(_ctx.$t('views.objectives.texts.setTarget')), 1))
              : (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(placeholder)))
        ])
      ]),
      _createElementVNode("td", _hoisted_10, [
        (_ctx.trendDirection != null)
          ? (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.trendDirection === 'improved' ? _unref(TrendingDown) : _unref(TrendingUp)), {
              key: 0,
              class: _normalizeClass(["h-4 w-4", _unref(trendDirectionColourClass)(_ctx.trendDirection)]),
              title: _ctx.trendDirection === 'improved' ? _ctx.$t('views.objectives.texts.trendImprovedTooltip') : _ctx.$t('views.objectives.texts.trendWorsenedTooltip'),
              "aria-hidden": "true"
            }, null, 8, ["class", "title"]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("td", _hoisted_11, [
        (_ctx.barn.pigsToTarget != null)
          ? (_openBlock(), _createElementBlock("span", {
              key: 0,
              class: _normalizeClass(["text-[11.5px] font-semibold", _unref(pigsToTargetColourClass)(_ctx.barn.pigsToTarget)]),
              title: _unref(pigsToTargetTooltipKey)(_ctx.barn.pigsToTarget) ? _ctx.$tc(`views.objectives.texts.${_unref(pigsToTargetTooltipKey)(_ctx.barn.pigsToTarget)}`, Math.abs(_ctx.barn.pigsToTarget)) : undefined
            }, _toDisplayString(_unref(formatPigsToTarget)(_ctx.barn.pigsToTarget)), 11, _hoisted_12))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("td", _hoisted_13, [
        _createElementVNode("span", _hoisted_14, _toDisplayString(_ctx.$t('views.objectives.texts.deadCountLabel', { count: _ctx.barn.deadCount })), 1)
      ])
    ]),
    (_ctx.barn.isExpandable && _ctx.expanded)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.barn.sections, (section) => {
            return (_openBlock(), _createBlock(SectionRow, {
              key: section.id,
              section: section,
              ceiling: _ctx.ceiling
            }, null, 8, ["section", "ceiling"]))
          }), 128)),
          (_ctx.barn.sections.length === 0)
            ? (_openBlock(), _createElementBlock("tr", _hoisted_15, [
                _createElementVNode("td", _hoisted_16, _toDisplayString(_ctx.$t('views.objectives.texts.noSections')), 1)
              ]))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true)
  ], 64))
}
}

})